import React, { useEffect, useState } from 'react';
import AppInputError from './input-error';
import CustomDropdown from './custom-dropdown';
import { useRateStore, useResourceStore } from '../../store';
import { numberFormat } from '../../utils/amount.util';

interface ExchangeInputProps {
  id: string;
  exchangeType: 'source' | 'destination';
  label?: string;
  required?: boolean;
  children?: React.ReactElement;
  value: string;
  currencyValue: string;
  icon?: string;
  onChange: (value: string) => void;
  onCurrencyChange: (value: string) => void;
  errorMessage?: string;
}

const ExchangeInput: React.FC<ExchangeInputProps> = (props) => {
  const {
    toCurrencies,
    fromCurrencies,
    getCountrySetupData,
    loading: loadingResource,
  } = useResourceStore();
  const { getActiveCountries } = useRateStore();

  const currencies =
    props.exchangeType === 'source' ? fromCurrencies : toCurrencies;

  const [amount, setAmount] = useState(props.value);
  const [selectedCurrency, setSelectedCurrency] = useState(props.currencyValue);

  useEffect(() => {
    getActiveCountries();
    getCountrySetupData();
  }, []);

  useEffect(() => {
    if (props.currencyValue !== selectedCurrency) {
      setSelectedCurrency(props.currencyValue);
    }
  }, [props.currencyValue]);

  useEffect(() => {
    if (currencies.length > 0) {
      const newCurrency = props.currencyValue || currencies[0].currency;
      if (newCurrency !== selectedCurrency) {
        setSelectedCurrency(newCurrency);
        props.onCurrencyChange(newCurrency);
      }
    }
  }, [currencies]);

  useEffect(() => {
    const formattedValue = numberFormat(props.value);
    setAmount(numberFormat(formattedValue));
  }, [props.value]);

  return (
    <div>
      {props.label && (
        <div className="mb-1 flex items-center gap-2">
          <p className="font-semibold text-xs">{props.label}</p>
          <p className={`text-red-500 ${props.required ? '' : 'invisible'}`}>
            *
          </p>
        </div>
      )}

      <div
        className={`w-full flex justify-start relative items-center rounded-xl border pl-4 pr-1 py-2 h-12 ${
          props.errorMessage ? 'border-red-500 bg-red-50' : ''
        }`}
      >
        <div className="w-3/5">
          {props.icon && props.children}
          <input
            id={`${props.id}-amount`}
            type="text"
            placeholder="0.00"
            className="flex w-full bg-transparent hover:outline-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 text-sm"
            value={amount}
            onChange={(e) => {
              const formattedValue = numberFormat(e.target.value);
              setAmount(numberFormat(formattedValue));
              props.onChange(formattedValue);
            }}
          />
        </div>
        <div className="w-2/5">
          <CustomDropdown
            id={`${props.id}-currency`}
            options={currencies.map((currency) => ({
              name: currency.currency,
              label: currency.currency,
              countryFlag: currency.flag,
            }))}
            selectedValue={selectedCurrency}
            onSelectChange={(value) => {
              setSelectedCurrency(value);
              props.onCurrencyChange(value);
            }}
            showCountryFlag
            isUsedForExchangeInput
            loading={loadingResource}
          />
        </div>
      </div>

      {props.errorMessage && <AppInputError text={props.errorMessage} />}
    </div>
  );
};

export default ExchangeInput;
